import * as d3 from 'd3-selection'
import * as array from 'd3-array'
import { EPSILON } from 'chart-constants'
import getLayout from './layout'
import breakdownSwap from './axis-breakdown-swap'
import ellipsis from 'ellipsis'

const labelSpacing = 12

function _ticks (range, ticks, tickWidth, scale) {
  ticks = Math.round(ticks)

  let r = []
    , step = Math.ceil(range.length / ticks)
    , maxWidth = scale.range()[1] + 2 // Add a 1px buffer on each side for rounding issues

  // We want to make sure the rightmost tick mark is visible. Try to use the last tick
  // in the range, but if it doesn't fit, we need to start working our way left to find the first tick
  // mark that will be visible
  let j = range.length - 1

  while (j >= 0 && (Math.floor(scale(range[j].id) + tickWidth) > maxWidth)) {
    j--
  }

  for (let i = j; i >= 0 && r.length < ticks; i -= step) {
    r.unshift(range[i]) // unshift to maintain correct dom order
  }

  return r
}

let dimensions = {
  rotatedLabelMaxWidth: 130,
  xAxisTitleHeight: 18,
  xAxisSubtitleHeight: 12,
  gridTopExtraPadding: 7 // extra padding on top when the chart is a rectangular grid (not polar or pie)
}

function getlongestTicklength (ticks, container) {
  let longest = ticks.reduce(function (p, c) {
      let aTitle = p.title || ''
        , bTitle = c.title || ''
        , a = aTitle.length > (p.subtitle && p.subtitle.length || '') ? aTitle : (p.subtitle || '')
        , b = bTitle.length > (c.subtitle && c.subtitle.length || '') ? bTitle : (c.subtitle || '')
      return a.length > b.length ? p : c
    })
    , placeholder = container.append('g').attr('class', 'tick')
    , result = 0

  if (longest.title || longest.subtitle) {
    let title = longest.title || ''
      , subtitle = longest.subtitle || ''
      , isSubtitle = title.length < subtitle.length // determine if the longest label is a title or subtitle for styling purposes

    // Add the placeholder with the correct class name, set the contents so we can determine width
    placeholder.append('text')
               .append('tspan')
               .classed('title', !isSubtitle)
               .classed('subtitle', isSubtitle)
               .text(isSubtitle ? longest.subtitle : longest.title)

    result = placeholder.node().getBBox().width

    // remove the temp node now that we have the width
    placeholder.remove()
  }

  return result
}

/*
 * This function is responsible for determining the x-axis title position and rotation
 */
function axisTitle(_x, title, chart, ticks, scaler, x, scalers) {

  const group = _x.selectAll('g.axis-x-title-group')
    .data(title && chart.layout.hasXAxis ? [title] : [])
    .enter()
    .append('g')
    .attr('class', 'axis-x-title-group')
    .attr('transform', () => {
      const y = -dimensions.gridTopExtraPadding

      let x = scaler.range()[1] / 2 // Default to the center of the group scale

      // If there are ticks, then we can center the title between the first and last tick
      let _ticks = ticks.selectAll('.tick'), nodes = _ticks.nodes()

      if (nodes.length >= 2) {
        let re = /\(([^)]+)\)/, first = Number(re.exec(nodes[0].getAttribute('transform'))[1].split(',')[0]), last = Number(re.exec(nodes[nodes.length - 1].getAttribute('transform'))[1].split(',')[0])

        x = (last - first) / 2 + first
      }

      return `translate(${x}, ${y})`
    })

    group.append('text')
      .attr('class', 'axis-x-title')
      .style('opacity', EPSILON)

  _x.selectAll('g.axis-x-title-group')
    .merge(group)
    .select('text.axis-x-title')
    .text(String)

  _x.selectAll('g.axis-x-title-group')
    .merge(group)
    .transition(chart.defaultTransition)
    .select(function () {
      const x = scaler.range()[1] / 2 // Center of the group scale
      const rotateTiles = this.getBBox().width > scalers.x.plotScale.bandwidth() // it doesn't fit

      if (rotateTiles) {
        // Set globablly since this axis code runs on each plot. The first
        // time this code runs is from `adjustDimensions`, with the longest plot title.
        // If the longest plot title doesn't fit, then we need to rotate all the titles
        // so it's consistent
        chart._rotateXTitles = true
      }

      // if it doesn't fit or _rotateXTitles was set
      if (chart._rotateXTitles) {
        d3.select(this)
          .classed('axis-rotated-labels', true)
          .attr('transform', `translate(${x}, ${-dimensions.gridTopExtraPadding}) rotate(-65)`)
          .select('text.axis-x-title')
          .call(ellipsis, dimensions.rotatedLabelMaxWidth)
      } else {
        d3.select(this)
          .classed('axis-rotated-labels', false)
          .attr('transform', `translate(${x}, ${-dimensions.gridTopExtraPadding})`)
      }
      return this
    })
    .select('text.axis-x-title')
    .transition(chart.defaultTransition)
    .style('opacity', 1)
}

/*
 * We need our own axis. The d3 component sets too many defaults that fail for our designs.
 */
function axis (selection, layout, axesConfig, dimensions, scalers, previousScaler, transition) {

  selection.each(function () {
    let group = d3.select(this)
      , rotate = false
      , tick = group.selectAll('.tick')
                    .data(function (d) {
                      if (!d.length) {
                        return []
                      }
                      let scale = scalers.x.groupScale.copy().range([0, scalers.x.plotScale.bandwidth()])
                        , placeholderWidth = getlongestTicklength(d, group)
                        , numTicks = Math.max(Math.abs(scale.range()[1] / (placeholderWidth + labelSpacing)), 2)

                      if (numTicks < d.length && axesConfig.bottom.showAllLabels) {
                        rotate = true
                        // We have to show all the labels but we don't have space.
                        // Send all the labels and we'll deal w/ rotating later
                        return d
                      }

                      return _ticks(d, numTicks, placeholderWidth, scale)
                    }, d => d.id)
      , tickEnter = tick.enter()
                        .append('g')
                          .attr('class', 'tick')
      , tickUpdate = tickEnter.merge(tick)
                              .order()
      , tickExit = tick.exit()

    // Position the x-axis group in its proper location
    group.transition(transition)
         .call(layout.axisTransform.bind(layout), dimensions.height)

    // Add the elements
    tickEnter.append('line')

    let textEnter = tickEnter.append('g')
                             .attr('class', 'text-group') // So we can apply transforms to the group (needed for RTL)
                             .append('text')
    textEnter.append('tspan').attr('class', 'title')
    textEnter.append('tspan').attr('class', 'subtitle')

    // Update tick text values outside of the transition
    let text = tickUpdate.select('text')
    text.select('.title').text(d => d.title)
    text.select('.subtitle').text(d => d.subtitle)

    tickEnter.call(layout.tickTransform.bind(layout), rotate, dimensions.height)

    if (transition && transition.duration()) {
      tickUpdate = tickUpdate.transition(transition)

      tickEnter.style('opacity', 1e-6)
               .call(layout.enterTransform.bind(layout))

      tickExit = tickExit.transition(transition)
                         .style('opacity', 1e-6)
                         .call(layout.exitTransform.bind(layout))
    }

    tickExit.remove()

    tickUpdate.style('opacity', 1)

    tickUpdate.call(layout.tickTransform.bind(layout), rotate, dimensions.height)
  })
}

/*
 * Builds an x-axis. The `scalers` object has both x and y scalers attached
 * to it. The y-scaler is used for some renderings of a x-axis, for example
 * the polar uses the y-scaler to determine how to layout the ticks
 */
export default function (scalers, chart) {
  let chartData = chart.datum()
    , axesConfig = chartData.axes
    , height = chartData.dimensions.height
    , scaler = scalers.x.groupScale

  let draw = function (selection) {
    let previousScaler = chart.previous && chart.previous.scalers.x.groupScale
      , startingHeight = chart.previous && chart.previous.dimensions.height || height
      , layout = new (getLayout(chart.layout.getTypes()))({
        previousScaler, // Just the groupScale
        scalers,
      })

    selection.each(function (plotData) {
      let title = plotData.breakdown && plotData.breakdown.title // Original title on the breakdown for this plot
        , plot = d3.select(this)
        , plotExiting = plot.classed('plot-exiting')
        , x = plot.selectAll('g.axis.axis-x')
                  .data(plotExiting ? [] : [plotData], d => d.id)
        , enter = x.enter()
                   .append('g')
                     .attr('class', 'axis axis-x')
                     .lower() // Make sure the x-axis is before all other nodes in its container
        , _x = enter.merge(d3.select(this).selectAll('g.axis.axis-x'))
                    .classed('no-labels', !axesConfig.bottom.labels)
        , ticks = _x.selectAll('g.axis-x-ticks')
                    .data(plot => {
                      if (!chart.layout.hasXAxis) {
                       return [[]]
                      }
                      let breakdown = chart.layout.breakdown.calculated ? chart.layout.breakdown : plot.breakdown

                      return [breakdown.range.map(d => d)] // Copy the range ticks by mapping. We need to figure out
                                                           // the previous bound data for smooth transitions.
                    })
                    .enter()
                      .append('g')
                      .attr('class', 'axis-x-ticks')
                      .call(layout.axisTransform.bind(layout), startingHeight)
                      .merge(x.selectAll('g.axis-x-ticks'))
                      .call(axis, layout, axesConfig, chartData.dimensions, scalers, previousScaler, chart.defaultTransition)
                      .transition(chart.defaultTransition)

      // TODO This should be improved so it uses the proper layout
      axisTitle(_x, title, chart, ticks, scaler, x, scalers)

      x.exit()
       .style('opacity', 1) // Make sure it's visible
       .transition(chart.defaultTransition)
       .style('opacity', EPSILON) // Then fade out
       .remove()

      // Exiting plots are removed immediately
      if (chartData.swappingBreakdowns) {
        // Invoke the x-axis breakdown swap code w/ the new axis
        enter.call(breakdownSwap, plot, scalers.x, chart)
      }
    })

  }

  return draw
}

/*
 * Returns an object that contains the heights of the ticks and title of the axis
 */
const heights = function (container, chart) {
  let result = {
    bottom: 0,
    top: 0
  }

  container.selectAll('.axis-x')
           .nodes()
           .forEach(function (axis) {
             let _axis = d3.select(axis)
               , ticks = _axis.selectAll('.axis-x-ticks')

             result.bottom = Math.max(array.max(ticks.selectAll('text').nodes(), node => {
               let measurement = ticks.classed('axis-rotated-labels') ? 'width' : 'height' // If the x-axis labels are rotated, then we need to use the width to determine occupied space
               return node.getBBox()[measurement]
             }) || 0, result.bottom)

             result.top = Math.max(array.max(_axis.selectAll('text.axis-x-title').nodes(), node => {
                let measurement = chart._rotateXTitles ? 'width' : 'height'
                return node.getBBox()[measurement] || 0
             }) || 0, result.top)

           })

  return result
}

export { dimensions, heights }
